import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError, fmtDate, DAY_SHORT, ROLES } from "@/lib/api";
import RecordsTable from "./RecordsTable";
import { CashPaidDialog } from "./CashPaidDialog";
import { EditRegistrationButton } from "./EditRegistration";

const days = (r) => (
  <div className="flex flex-wrap gap-1">
    {r.days.map((d) => <span key={d} className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300">{DAY_SHORT[d] || d}</span>)}
  </div>
);
const person = (r) => (
  <div>
    <p className="font-semibold text-neutral-100">{r.name}</p>
    <p className="text-xs text-neutral-500">{r.email}</p>
    <p className="text-xs text-neutral-500">{r.phone}</p>
  </div>
);
const created = { key: "created_at", label: "Created", render: (r) => <span className="whitespace-nowrap text-xs">{fmtDate(r.created_at)}</span> };
const personCol = { key: "name", label: "Name / Contact", render: person };

const STATUS_CLS = {
  pending_payment: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  paid: "bg-emerald-600/20 text-emerald-300 border-emerald-600/50",
};
const StatusBadge = ({ s, id }) => (
  <span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[s] || "border-neutral-700 text-neutral-400"}`} data-testid={`donation-status-${id}`}>
    {s === "pending_payment" ? "Pending payment" : s === "paid" ? "Paid" : s}
  </span>
);

export const RegistrationsPage = () => (
  <RecordsTable
    name="registrations"
    title="Registrants"
    filters={["day", "opt_in", "volunteer"]}
    columns={[
      created,
      personCol,
      { key: "party_size", label: "Party", render: (r) => <b className="font-display text-lg text-gold">{r.party_size}</b> },
      { key: "days", label: "Days", render: days },
      { key: "opt_in", label: "Opt-in", render: (r) => (r.opt_in ? "Yes" : "No") },
      { key: "volunteer", label: "Volunteer", render: (r) => <span className={r.volunteer ? "font-semibold text-emerald-400" : ""} data-testid={`registrant-volunteer-${r.id}`}>{r.volunteer ? "Yes" : "No"}</span> },
      { key: "donated", label: "Donated", render: (r) => <span className={r.donated ? "font-display text-base text-gold" : "text-neutral-500"} data-testid={`registrant-donated-${r.id}`}>{r.donated ? `$${r.donated.toFixed(2)}` : "—"}</span> },
      { key: "giveaway", label: "Giveaway", render: (r) => <span className={r.giveaway ? "font-semibold text-emerald-400" : ""} data-testid={`registrant-giveaway-${r.id}`}>{r.giveaway ? "Yes" : "No"}</span> },
      { key: "signature", label: "Signed", render: (r) => <span className="font-script">{r.signature}</span> },
    ]}
    actions={(r, reload) => <EditRegistrationButton reg={r} onSaved={reload} />}
  />
);

export const VolunteersPage = () => (
  <RecordsTable
    name="volunteers"
    title="Volunteers"
    filters={["day", "role"]}
    columns={[
      created,
      personCol,
      { key: "days", label: "Available", render: days },
      { key: "role", label: "Role", render: (r) => ROLES.find((x) => x.value === r.role)?.label || r.role },
      { key: "notes", label: "Notes", render: (r) => <p className="max-w-xs text-xs">{r.notes}</p> },
    ]}
  />
);

export const ContactsPage = () => (
  <RecordsTable
    name="contacts"
    title="Contact messages"
    columns={[created, personCol, { key: "message", label: "Message", render: (r) => <p className="max-w-md whitespace-pre-wrap text-xs">{r.message}</p> }]}
  />
);

export const DonationsPage = () => (
  <RecordsTable
    name="donations"
    title="Donations"
    filters={["status"]}
    columns={[
      created,
      { key: "name", label: "Registrant", render: person },
      { key: "amount", label: "Amount", render: (r) => (r.amount != null ? <b className="font-display text-lg text-gold" data-testid={`donation-amount-${r.id}`}>${r.amount.toFixed(2)}</b> : <span className="text-neutral-500">—</span>) },
      { key: "status", label: "Status", render: (r) => <StatusBadge s={r.status} id={r.id} /> },
      { key: "method", label: "Method", render: (r) => <span className="capitalize">{r.method}</span> },
      { key: "stripe_session_id", label: "Stripe ref", render: (r) => <span className="block max-w-[140px] truncate font-mono text-[11px] text-neutral-500" title={r.stripe_session_id || ""}>{r.stripe_session_id || "—"}</span> },
    ]}
    actions={(r, reload) => r.status !== "paid" && <CashPaidDialog donation={r} onDone={reload} />}
  />
);

const ManualEntryDialog = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", note: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/giveaway/manual", f);
      toast.success("Free entry added");
      setF({ name: "", email: "", phone: "", note: "" });
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(formatError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex h-10 items-center gap-2 rounded-full bg-amber-500 px-4 text-sm font-semibold text-neutral-950 hover:bg-amber-400" data-testid="giveaway-add-manual-button">
          <Plus className="h-4 w-4" /> Add free entry
        </button>
      </DialogTrigger>
      <DialogContent className="border-neutral-800 bg-neutral-950">
        <DialogHeader><DialogTitle className="font-display text-2xl text-gold">Manual free entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="giveaway-manual-form">
          {[["name", "Name", "text"], ["email", "Email", "email"], ["phone", "Phone", "tel"], ["note", "Note", "text"]].map(([k, l, t]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`gm-${k}`}>{l}</Label>
              <Input id={`gm-${k}`} type={t} required={k === "name"} value={f[k]} onChange={set(k)} className="border-neutral-800 bg-neutral-900" data-testid={`giveaway-manual-${k}-input`} />
            </div>
          ))}
          <p className="text-xs text-neutral-500">Email or phone is required. One entry per family.</p>
          {error && <p className="text-sm text-red-400" data-testid="giveaway-manual-error">{error}</p>}
          <button type="submit" className="h-11 w-full rounded-full bg-amber-500 font-display font-semibold text-neutral-950" data-testid="giveaway-manual-submit">Add entry</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const GiveawayPage = () => {
  const [key, setKey] = useState(0);
  return (
    <RecordsTable
      name="giveaway"
      title="Giveaway entries"
      filters={["source"]}
      reloadKey={key}
      headerExtra={<ManualEntryDialog onAdded={() => setKey((k) => k + 1)} />}
      columns={[
        created,
        personCol,
        { key: "source", label: "Source", render: (r) => <span className="capitalize">{r.source === "manual" ? "Manual / free" : "Verified donation"}</span> },
        { key: "note", label: "Note", render: (r) => <span className="text-xs">{r.note || "—"}</span> },
      ]}
    />
  );
};
